import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { ChartTooltipModel }         from '../typings/intelligence.chart-tooltip';
import { DonutSegmentModel }         from '../typings/intelligence.donut-segment';
import { IntelligenceFormatService } from '../intelligence.format.service';

interface RenderedSegment {
  color     : string;
  dashArray : string;
  dashOffset: number;
  label     : string;
  percent   : number;
  segment   : DonutSegmentModel;
  value     : number;
}

@Component({
  selector   : 'wor-intelligence-donut-chart',
  template: require('./intelligence.donut-chart.component.html')
})
export class DonutChartComponent implements OnChanges {
  @Input() centerLabel : string = '';
  @Input() format      : string = 'money';
  @Input() segments    : Array<DonutSegmentModel> = [];

  @Output() segmentPressed = new EventEmitter<DonutSegmentModel>();

  public circumference = 2 * Math.PI * 70;
  public expanded   : boolean = false;
  public hoverIndex : number = -1;
  public rendered   : Array<RenderedSegment> = [];
  public tooltip    : ChartTooltipModel = { rows: [], title: '', visible: false, x: 0, y: 0 };
  public total      : number = 0;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService
  ) {}

  ngOnChanges () : void {
    this.expanded = false;
    this.render();
  }

  collapse () : void {
    this.expanded = false;
    this.render();
  }

  formatValue ( value : number ) : string {
    return this.format === 'count'
      ? this.intelligenceFormatService.count(value)
      : this.intelligenceFormatService.money(value);
  }

  hideTooltip () : void {
    this.hoverIndex = -1;
    this.tooltip = { ...this.tooltip, visible: false };
  }

  isDrillable ( rendered : RenderedSegment ) : boolean {
    return this.isExpandable(rendered.segment)
      || ( !!rendered.segment.drillable && this.segmentPressed.observers.length > 0 );
  }

  onMove ( event : MouseEvent, segment : RenderedSegment, index : number ) : void {
    const wrapper = ( event.currentTarget as Element ).closest('.intelligence-chart-wrapper') as HTMLElement;
    const bounds = wrapper.getBoundingClientRect();

    this.hoverIndex = index;

    this.tooltip = {
      rows: [{
        color: segment.color,
        label: segment.label,
        prior: `${ segment.percent.toFixed(1) }%`,
        value: this.formatValue(segment.value)
      }],
      title  : segment.label,
      visible: true,
      x      : Math.min(event.clientX - bounds.left + 14, bounds.width - 170),
      y      : Math.max(event.clientY - bounds.top - 10, 4)
    };
  }

  press ( rendered : RenderedSegment ) : void {
    if ( this.isExpandable(rendered.segment) ) {
      this.expanded = true;
      this.render();

      return;
    }

    if ( !!rendered.segment.drillable && this.segmentPressed.observers.length > 0 ) {
      this.segmentPressed.emit(rendered.segment);
    }
  }

  // The Other slice opens into its own donut of the folded entries, so the tail is
  // explorable without crowding the main view past the palette.
  private isExpandable ( segment : DonutSegmentModel ) : boolean {
    return !this.expanded && !!segment.others && segment.others.length > 0;
  }

  private render () : void {
    const other = this.segments.find(segment => segment.others && segment.others.length > 0);
    const source = this.expanded && other
      ? other.others
      : this.segments;

    this.hideTooltip();
    this.rendered = [];
    this.total = source.reduce(( sum, segment ) => sum + Math.max(segment.value, 0), 0);

    if ( this.total <= 0 ) {
      return;
    }

    let offset = 0;

    this.rendered = source
      .filter(segment => segment.value > 0)
      .map(segment => {
        const percent = ( segment.value / this.total ) * 100;
        const length = ( segment.value / this.total ) * this.circumference;
        const item = {
          color     : segment.color,
          dashArray : `${ Math.max(length - 2, 0) } ${ this.circumference - Math.max(length - 2, 0) }`,
          dashOffset: -offset,
          label     : segment.label,
          percent,
          segment,
          value     : segment.value
        };

        offset += length;

        return item;
      });
  }
}
